/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import { center as turf_center } from '@turf/turf';
import { dateUtils } from '@opengeoweb/shared';
import type { CoreAppStore } from '../types';
import { selectorMemoizationOptions } from '../utils';
import { SYNCGROUPS_TYPE_SETTIME } from './syncGroups/constants';
import type {
  LinkedState,
  PotentialData,
  SynchronizationGroupState,
  SynchronizationSource,
} from './syncGroups/types';
import type { SetTimePayload } from './types';
import type { TimeSliderStartEndStepCurrent } from '../map/types';

const synchronizationGroupStore = (
  store: CoreAppStore,
): SynchronizationGroupState => (store?.syncGroups ? store.syncGroups : null!);

export const getSynchronizationGroupStore = (
  store: CoreAppStore,
): SynchronizationGroupState => {
  return store?.syncGroups ? store.syncGroups : null!;
};

/**
 * Returns the synchronization source by id
 * @param store The app  store
 * @param id The source id to find
 */
const getSyncSourceBySourceId = (
  state: SynchronizationGroupState,
  id: string,
): SynchronizationSource => {
  if (state?.sources) {
    const { sources } = state;
    if (sources.byId[id]) {
      return sources.byId[id];
    }
  }
  return null!;
};

export const getTime = createSelector(
  getSyncSourceBySourceId,
  (store) =>
    store?.payloadByType?.[SYNCGROUPS_TYPE_SETTIME]
      ? (store.payloadByType[SYNCGROUPS_TYPE_SETTIME] as SetTimePayload).value
      : null,
  selectorMemoizationOptions,
);

const selectLinkedState = createSelector(
  synchronizationGroupStore,
  (syncState) => {
    return syncState?.linkedState || { links: {}, sharedData: {} };
  },
);

export const getLinkedMaps = createSelector(
  [selectLinkedState, (store, panelId?: string): string | undefined => panelId],
  (linkedState, panelId): string[] =>
    (panelId && linkedState.links[panelId]) || [],
  selectorMemoizationOptions,
);

const getLinkedFeatureProviders = createSelector(
  (linkedState: LinkedState) => linkedState,
  (linkedState: LinkedState, props: { mapId: string }) => props.mapId,
  (linkedState, mapId): string[] => {
    return linkedState
      ? Object.keys(linkedState.links).filter((panelId) =>
          linkedState.links[panelId].includes(mapId),
        )
      : [];
  },
);

export const selectSharedData = createSelector(
  [selectLinkedState, (store, panelId: string): string => panelId],
  (linkedState, panelId) => {
    const sharedDataForPanel = linkedState.sharedData[panelId];

    return sharedDataForPanel || {};
  },
  selectorMemoizationOptions,
);

export const selectLinkedPanelId = createSelector(
  [selectLinkedState, (_, mapViewId: string): string => mapViewId],
  (linkedState, mapViewId): string | undefined => {
    if (!linkedState?.links) {
      return undefined;
    }

    // Find the panelId where mapView(s) is linked
    const panelId = Object.keys(linkedState.links).find((panelId) =>
      linkedState.links[panelId]?.includes(mapViewId),
    );

    return panelId;
  },
);

export const selectMapViewsForPanelId = createSelector(
  [
    (linkedState: LinkedState, panelId: string): string[] =>
      linkedState.links[panelId],
  ],
  (mapViews): string[] | undefined => {
    if (!mapViews) {
      return undefined;
    }
    return mapViews;
  },
);

export const selectLinkedFeatures = createSelector(
  [
    (linkedState): LinkedState | undefined => linkedState as LinkedState,
    getLinkedFeatureProviders,
  ],
  (linkedState, featureProviders) => {
    const features = featureProviders.flatMap(
      (providerId) => linkedState?.sharedData?.[providerId]?.features ?? [],
    );
    return features.filter((feature) => feature !== undefined);
  },
);

export const selectLinkedFormFeatures = createSelector(
  [
    (linkedState): LinkedState | undefined => linkedState as LinkedState,
    getLinkedFeatureProviders,
  ],
  (linkedState, featureProviders) => {
    const features = featureProviders.flatMap(
      (providerId) => linkedState?.sharedData?.[providerId]?.formFeatures ?? [],
    );
    return features.filter((feature) => feature !== undefined);
  },
);

export const getSelectedFeature = createSelector(
  [
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    (state: SynchronizationGroupState, mapId: string) => {
      return state.linkedState?.sharedData[mapId];
    },
  ],
  (sharedData) => {
    if (!sharedData) {
      return null;
    }
    const { features, selectedFeatureId } = sharedData;
    if (!features || !selectedFeatureId) {
      return null;
    }
    const selectedFeature = features[0]?.geoJSON.features.find(
      (feature) => feature.id === selectedFeatureId,
    );

    if (selectedFeature?.geometry.type === 'Point') {
      const coordinates = selectedFeature.geometry.coordinates as [
        number,
        number,
      ];
      return {
        lat: coordinates[1],
        lon: coordinates[0],
        id: selectedFeature.id as string,
        name: selectedFeature.properties?.name,
        occurrences: selectedFeature.properties?.occurrences,
        serviceId: selectedFeature.properties?.serviceId,
      };
    }

    if (selectedFeature) {
      const center = turf_center(selectedFeature);

      return {
        lat: center.geometry.coordinates[1],
        lon: center.geometry.coordinates[0],
        id: selectedFeature.id as string,
        name: selectedFeature.properties?.name,
        occurrences: selectedFeature.properties?.occurrences,
      };
    }

    return null;
  },
);

export const getSelectedFeatureAsGeoJSON = createSelector(
  [
    (state: SynchronizationGroupState, mapId: string): PotentialData => {
      return state.linkedState?.sharedData[mapId];
    },
  ],
  (sharedData) => {
    if (!sharedData) {
      return null;
    }
    const { features, selectedFeatureId } = sharedData;
    if (!features || !selectedFeatureId) {
      return null;
    }
    const selectedFeature = features[0]?.geoJSON.features.find(
      (feature) => feature.id === selectedFeatureId,
    );
    return selectedFeature;
  },
);

export const getSyncedStartCenterEndStepCurrentTime = createSelector(
  (coreAppStore, sourceId) =>
    getTime(getSynchronizationGroupStore(coreAppStore), sourceId),
  (store): TimeSliderStartEndStepCurrent | undefined => {
    if (!store?.timeSliderCenterTime || !store?.timeSliderSpan) {
      return undefined;
    }

    return {
      start: Math.round(store.timeSliderCenterTime - store.timeSliderSpan / 2),
      end: Math.round(store.timeSliderCenterTime + store.timeSliderSpan / 2),
      step: store?.timeSliderStep || 60,
      current: dateUtils.unixFromISOString(store?.currentTime),
    } as TimeSliderStartEndStepCurrent;
  },
  selectorMemoizationOptions,
);
